/**
 * Side-nav and top-bar glyphs, copied path for path from the Console V3 design so the
 * shell uses the design's own icons and nothing invented. Decorative: every one sits
 * beside a text label, so they are hidden from screen readers.
 */
import type { ReactNode } from 'react';
import type { ViewId } from '@aithora/shared';

/**
 * `opacity`: the design draws most nav glyphs at .72 (the .navitem .glyph rule) but a few
 * at full strength (Performance, System Pulse, Collapse); `join` false leaves the design's
 * miter joins where its svg sets no stroke-linejoin.
 */
function Glyph({ size = 17, width = 1.7, opacity, join = true, children }: { size?: number; width?: number; opacity?: number; join?: boolean; children: ReactNode }) {
  return (
    <svg
      className="glyph"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin={join ? 'round' : undefined}
      style={opacity === undefined ? undefined : { opacity }}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

const grid = (
  <>
    <rect x="3" y="3" width="7" height="7" rx="1.6" />
    <rect x="14" y="3" width="7" height="7" rx="1.6" />
    <rect x="3" y="14" width="7" height="7" rx="1.6" />
    <rect x="14" y="14" width="7" height="7" rx="1.6" />
  </>
);

export const NAV_ICON: Record<ViewId, ReactNode> = {
  overview: <Glyph>{grid}</Glyph>,
  home: (
    <Glyph>
      <path d="M3.6 10.4 12 3.6l8.4 6.8v9.2a1.4 1.4 0 0 1-1.4 1.4H5a1.4 1.4 0 0 1-1.4-1.4Z" />
      <path d="M9.4 20.4v-7.2h5.2v7.2" />
    </Glyph>
  ),
  companion: (
    <Glyph>
      <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4L3 21l1.1-4.2A8.4 8.4 0 1 1 21 11.5Z" />
    </Glyph>
  ),
  residents: (
    <Glyph>
      <circle cx="9" cy="8" r="3.4" />
      <path d="M2.8 20a6.2 6.2 0 0 1 12.4 0" />
      <path d="m16.5 11 1.8 1.8 3.4-3.6" />
    </Glyph>
  ),
  guardian: (
    <Glyph>
      <path d="M12 3 4.5 6v5.6c0 4.6 3.1 8.1 7.5 9.4 4.4-1.3 7.5-4.8 7.5-9.4V6Z" />
    </Glyph>
  ),
  worklist: (
    <Glyph>
      <rect x="5" y="3.5" width="14" height="17" rx="2.2" />
      <path d="M9 3.5V2.8h6v.7" />
      <path d="M9 9.5h6M9 13.5h6M9 17.5h3" />
    </Glyph>
  ),
  family: (
    <Glyph>
      <path d="M12 20.3S3.8 15.4 3.8 9.7A4.4 4.4 0 0 1 12 7.3a4.4 4.4 0 0 1 8.2 2.4c0 5.7-8.2 10.6-8.2 10.6Z" />
    </Glyph>
  ),
  pulse: (
    <Glyph size={15} width={1.8} opacity={1} join={false}>
      <path d="M2.5 12h4l2-5 3 10 2.5-7 2 4h5.5" />
    </Glyph>
  ),
  oversight: <Glyph>{grid}</Glyph>,
  atlas: (
    <Glyph>
      <circle cx="12" cy="12" r="8.6" />
      <circle cx="12" cy="12" r="2.6" />
    </Glyph>
  ),
  policy: (
    <Glyph>
      <path d="M12 2.8 21.2 12 12 21.2 2.8 12Z" />
    </Glyph>
  ),
  finance: (
    <Glyph>
      <path d="M12 2.6v18.8" />
      <path d="M16.6 6.4H9.9a3.1 3.1 0 0 0 0 6.2h4.2a3.1 3.1 0 0 1 0 6.2H7" />
    </Glyph>
  ),
  capture: (
    <Glyph>
      <path d="M6 3.5h12v17l-3-2-3 2-3-2-3 2z" />
      <path d="M9 9.5l2 2 4-4" />
      <path d="M9 14.5h6" />
    </Glyph>
  ),
  performance: (
    <Glyph opacity={1}>
      <path d="M4.6 20.4V13" />
      <path d="M10.2 20.4V4.4" />
      <path d="M15.8 20.4V9.6" />
      <path d="M21.4 20.4v-5.2" />
    </Glyph>
  ),
  atlasIntel: (
    <Glyph>
      <path d="M3.4 20.4h17.2" />
      <path d="M4.8 20.4V10.2L12 5.2l7.2 5v10.2" />
      <path d="M10 20.4v-5.2h4v5.2" />
    </Glyph>
  ),
  sales: (
    <Glyph>
      <rect x="2.8" y="7.2" width="18.4" height="12.6" rx="2.2" />
      <path d="M8.6 7.2V5.8a2 2 0 0 1 2-2h2.8a2 2 0 0 1 2 2v1.4" />
    </Glyph>
  ),
  settings: (
    <Glyph>
      <circle cx="12" cy="12" r="3.1" />
      <path d="M19.5 14.6a1.6 1.6 0 0 0 .32 1.77l.06.06a1.95 1.95 0 1 1-2.76 2.76l-.06-.06a1.6 1.6 0 0 0-1.77-.32 1.6 1.6 0 0 0-.97 1.47v.16a1.95 1.95 0 1 1-3.9 0v-.09a1.6 1.6 0 0 0-1.05-1.47 1.6 1.6 0 0 0-1.77.32l-.06.06a1.95 1.95 0 1 1-2.76-2.76l.06-.06a1.6 1.6 0 0 0 .32-1.77 1.6 1.6 0 0 0-1.47-.97h-.16a1.95 1.95 0 1 1 0-3.9h.09a1.6 1.6 0 0 0 1.47-1.05 1.6 1.6 0 0 0-.32-1.77l-.06-.06a1.95 1.95 0 1 1 2.76-2.76l.06.06a1.6 1.6 0 0 0 1.77.32h.08a1.6 1.6 0 0 0 .97-1.47v-.16a1.95 1.95 0 1 1 3.9 0v.09a1.6 1.6 0 0 0 .97 1.47 1.6 1.6 0 0 0 1.77-.32l.06-.06a1.95 1.95 0 1 1 2.76 2.76l-.06.06a1.6 1.6 0 0 0-.32 1.77v.08a1.6 1.6 0 0 0 1.47.97h.16a1.95 1.95 0 1 1 0 3.9h-.09a1.6 1.6 0 0 0-1.47.97Z" />
    </Glyph>
  ),
  help: (
    <Glyph>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.6 9.2a2.5 2.5 0 1 1 3.6 2.3c-.7.4-1.2 1-1.2 1.8" />
      <path d="M12 17h.01" />
    </Glyph>
  ),
};

/** The top bar's ? (T182): round caps, no round joins. */
export const HelpIcon = () => (
  <Glyph join={false}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.6 9.2a2.5 2.5 0 1 1 3.6 2.3c-.7.4-1.2 1-1.2 1.8" />
    <path d="M12 17h.01" />
  </Glyph>
);

export const AtlasIcon = () => (
  <Glyph size={14} width={1.8}>
    <circle cx="12" cy="12" r="8.6" />
    <circle cx="12" cy="12" r="2.6" />
  </Glyph>
);

export const BellIcon = () => (
  <Glyph>
    <path d="M6 8a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8" />
    <path d="M10.3 20a1.9 1.9 0 0 0 3.4 0" />
  </Glyph>
);

export const SignOutIcon = () => (
  <Glyph size={14} width={1.8}>
    <path d="M14.5 16.5 19 12l-4.5-4.5" />
    <path d="M19 12H8.5" />
    <path d="M11 4.5H6.2A2 2 0 0 0 4.2 6.5v11a2 2 0 0 0 2 2H11" />
  </Glyph>
);

export const CollapseIcon = ({ flipped }: { flipped?: boolean }) => (
  <Glyph size={15} width={1.8} opacity={1}>
    <path d={flipped ? 'M9 5.5 15.5 12 9 18.5' : 'M15 5.5 8.5 12l6.5 6.5'} />
  </Glyph>
);
